<template>
  <div class="card mt-3">
    <div class="pb-0 card-header mb-0">
      <div class="d-flex flex-wrap justify-content-between align-items-start gap-2">
        <div>
          <h5 class="mb-1">Productos más vendidos</h5>
          <p class="text-sm mb-0">
            <span class="font-weight-bold">{{ periodLabel }}</span>
            <span v-if="products.length"> · {{ products.length }} producto(s)</span>
          </p>
        </div>
        <select v-model="periodFilter" class="form-select form-select-sm" style="width: auto; min-width: 9rem;">
          <option value="day">Hoy</option>
          <option value="week">Esta semana</option>
          <option value="month">Este mes</option>
        </select>
      </div>
    </div>
    <div class="p-3 card-body">
      <div v-if="loading" class="text-center py-2 text-muted text-sm">Cargando productos...</div>
      <div v-if="!loading && products.length === 0" class="text-center py-5 text-muted">
        No hay ventas de productos en este periodo.
      </div>
      <div v-show="products.length > 0" class="chart" :style="{ height: chartHeight + 'px' }">
        <canvas ref="productsChart" class="chart-canvas"></canvas>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from "chart.js/auto";

export default {
  name: "top-products-chart",
  data() {
    return {
      periodFilter: "month",
      products: [],
      loading: false,
      chart: null,
    };
  },
  computed: {
    periodLabel() {
      const labels = {
        day: "Ventas de hoy",
        week: "Ventas de esta semana",
        month: "Ventas de este mes",
      };
      return labels[this.periodFilter] || labels.month;
    },
    chartHeight() {
      const count = Math.max(this.products.length, 1);
      return Math.min(Math.max(count * 48 + 80, 240), 520);
    },
  },
  watch: {
    periodFilter() {
      this.fetchProducts();
    },
  },
  methods: {
    formatUnits(value) {
      const num = Number(value) || 0;
      return Number.isInteger(num) ? String(num) : num.toLocaleString("es-CO", { maximumFractionDigits: 2 });
    },
    truncateLabel(name, max = 40) {
      if (!name) return "Sin nombre";
      return name.length > max ? `${name.slice(0, max)}…` : name;
    },
    async fetchProducts() {
      this.loading = true;
      try {
        const response = await this.$store.dispatch("analitycs/getTopProducts", {
          period: this.periodFilter,
          limit: 10,
        });
        this.products = response.data.products || [];
      } catch (error) {
        console.error("Error al cargar productos más vendidos:", error);
        this.products = [];
      } finally {
        this.loading = false;
        await this.$nextTick();
        this.updateChart();
      }
    },
    updateChart() {
      if (!this.products.length) {
        if (this.chart) {
          this.chart.destroy();
          this.chart = null;
        }
        return;
      }

      const canvas = this.$refs.productsChart;
      if (!canvas) return;

      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }

      const products = this.products;
      const formatUnits = this.formatUnits;
      const truncateLabel = this.truncateLabel.bind(this);

      this.chart = new Chart(canvas.getContext("2d"), {
        type: "bar",
        data: {
          labels: products.map((p) => truncateLabel(p.name)),
          datasets: [
            {
              label: "Cantidad vendida",
              data: products.map((p) => Number(p.units_sold) || 0),
              backgroundColor: "rgba(94, 114, 228, 0.8)",
              borderRadius: 4,
              barThickness: 20,
            },
          ],
        },
        options: {
          indexAxis: "y",
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: { right: 40 },
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              callbacks: {
                title(items) {
                  const index = items[0]?.dataIndex ?? 0;
                  return products[index]?.name || "";
                },
                label(context) {
                  const product = products[context.dataIndex];
                  if (!product) return "";

                  const stock = product.manage_stock
                    ? formatUnits(product.stock)
                    : "sin control";

                  return [
                    `Cantidad vendida: ${formatUnits(product.units_sold)}`,
                    `Nº de ventas: ${product.sales_count}`,
                    `Stock disponible: ${stock}`,
                  ];
                },
              },
            },
          },
          scales: {
            x: {
              beginAtZero: true,
              title: {
                display: true,
                text: "Cantidad vendida",
                color: "#67748e",
                font: { size: 11 },
              },
              grid: {
                drawBorder: false,
                borderDash: [4, 4],
              },
              ticks: {
                precision: 0,
                color: "#67748e",
              },
            },
            y: {
              grid: {
                display: false,
              },
              ticks: {
                color: "#344767",
                font: { size: 11 },
                autoSkip: false,
              },
            },
          },
        },
        plugins: [
          {
            id: "barEndLabels",
            afterDatasetsDraw(chart) {
              const { ctx, chartArea } = chart;
              const meta = chart.getDatasetMeta(0);

              ctx.save();
              ctx.fillStyle = "#344767";
              ctx.font = "11px Open Sans, sans-serif";
              ctx.textAlign = "left";
              ctx.textBaseline = "middle";

              meta.data.forEach((bar, index) => {
                const value = chart.data.datasets[0].data[index];
                if (value == null) return;

                const text = formatUnits(value);
                const x = Math.min(bar.x + 6, chartArea.right - 4);
                ctx.fillText(text, x, bar.y);
              });

              ctx.restore();
            },
          },
        ],
      });
    },
  },
  mounted() {
    this.fetchProducts();
  },
  beforeUnmount() {
    if (this.chart) {
      this.chart.destroy();
    }
  },
};
</script>
